(() => {
  'use strict';
  // Client-only porch photo booth, revision 2026-10-24.

  const MAX_BYTES = 12 * 1024 * 1024;
  const MAX_EDGE = 12000;
  const MAX_PIXELS = 60000000;
  const WORK_EDGE = 2400;
  const SIZE = 1080;
  const OUTPUT_SIZE = 1200;
  const PHOTO = { x: 265, y: 128, width: 548, height: 645 };
  const PLATE = { x: 245, y: 789, width: 590, height: 119 };
  const instances = new WeakMap();
  const palettes = {
    pumpkin: { background: '#fff4de', photo: '#f3e0c2', line: '#b97643', ink: '#362536', accent: '#e59a57' },
    ghost: { background: '#302435', photo: '#443244', line: '#e4bd84', ink: '#fff4de', accent: '#fff4df' },
    bat: { background: '#271b31', photo: '#3b2a40', line: '#d2ab73', ink: '#fff4de', accent: '#efb875' }
  };

  const message = (state, key) => state.root.dataset[key] || '';
  const backgroundKeys = {
    pumpkin: 'porchBackgroundUrl',
    ghost: 'porchGhostBackgroundUrl',
    bat: 'porchBatBackgroundUrl'
  };
  const selectedFrame = state => {
    const selected = state.frames.find(control => control.checked)?.value;
    return Object.hasOwn(palettes, selected) ? selected : 'pumpkin';
  };
  const number = (value, fallback = 50) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : fallback;
  };
  const cleanName = value => Array.from(String(value || '').replace(/[\u0000-\u001f\u007f]/g, '').trim()).slice(0, 24).join('');
  const photoName = state => cleanName(state.name?.value) || message(state, 'defaultName');

  function setStatus(state, key, error = false) {
    state.statusKey = key;
    if (state.status) {
      state.status.textContent = message(state, key);
      state.status.dataset.error = String(error);
    }
    state.file.setAttribute('aria-invalid', String(error && ['uploadError', 'tooLarge', 'formatError', 'resolutionError'].includes(key)));
  }

  function syncControls(state) {
    const ready = Boolean(state.image) && !state.loading && !state.exporting && !state.backgroundLoading;
    state.download.disabled = !ready;
    state.download.setAttribute('aria-busy', String(state.exporting || state.backgroundLoading));
    if (state.clear) {
      state.clear.disabled = !state.image && !state.loading;
      state.clear.hidden = !state.image && !state.loading;
    }
    const positioning = Boolean(state.image) && state.fit?.value === 'cover';
    const scale = positioning ? Math.max(PHOTO.width / state.image.width, PHOTO.height / state.image.height) : 0;
    const canMoveX = positioning && state.image.width * scale - PHOTO.width > 0.5;
    const canMoveY = positioning && state.image.height * scale - PHOTO.height > 0.5;
    if (state.position) state.position.hidden = !canMoveX && !canMoveY;
    if (state.x) state.x.disabled = !canMoveX;
    if (state.y) state.y.disabled = !canMoveY;
  }

  function roundRect(ctx, x, y, width, height, radius) {
    const r = Math.min(radius, width / 2, height / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + width, y, x + width, y + height, r);
    ctx.arcTo(x + width, y + height, x, y + height, r);
    ctx.arcTo(x, y + height, x, y, r);
    ctx.arcTo(x, y, x + width, y, r);
    ctx.closePath();
  }

  function paw(ctx, x, y, scale, color) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.fillStyle = color;
    [[-26, -20, -0.4], [-9, -34, -0.12], [12, -31, 0.18], [30, -15, 0.45]].forEach(([px, py, rotation]) => {
      ctx.beginPath();
      ctx.ellipse(px, py, 9, 13, rotation, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.beginPath();
    ctx.moveTo(-26, 18);
    ctx.bezierCurveTo(-26, 4, -14, -7, -5, -11);
    ctx.bezierCurveTo(6, -18, 15, -4, 23, 6);
    ctx.bezierCurveTo(37, 29, 16, 34, 2, 27);
    ctx.bezierCurveTo(-11, 35, -29, 32, -26, 18);
    ctx.fill();
    ctx.restore();
  }

  function pumpkin(ctx, x, y, scale) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.strokeStyle = '#7a5136';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(0, -24);
    ctx.quadraticCurveTo(-5, -39, 6, -43);
    ctx.stroke();
    [-16, 16, 0].forEach((offset, index) => {
      ctx.fillStyle = index === 2 ? '#e99a50' : '#cf763c';
      ctx.beginPath();
      ctx.ellipse(offset, 0, index === 2 ? 22 : 21, 30, 0, 0, Math.PI * 2);
      ctx.fill();
    });
    paw(ctx, 0, 7, 0.48, '#5f3b2b');
    ctx.restore();
  }

  function bat(ctx, x, y, scale, color) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(49, 26);
    ctx.lineTo(48, 8);
    ctx.lineTo(60, 20);
    ctx.lineTo(72, 8);
    ctx.lineTo(71, 26);
    ctx.bezierCurveTo(86, 20, 99, 11, 116, 11);
    ctx.lineTo(109, 37);
    ctx.quadraticCurveTo(99, 31, 95, 45);
    ctx.quadraticCurveTo(84, 35, 80, 51);
    ctx.quadraticCurveTo(69, 43, 65, 63);
    ctx.lineTo(60, 58);
    ctx.lineTo(55, 63);
    ctx.quadraticCurveTo(51, 43, 40, 51);
    ctx.quadraticCurveTo(36, 35, 25, 45);
    ctx.quadraticCurveTo(21, 31, 11, 37);
    ctx.lineTo(4, 11);
    ctx.bezierCurveTo(21, 11, 34, 20, 49, 26);
    ctx.fill();
    ctx.fillStyle = '#302435';
    [55, 65].forEach(px => {
      ctx.beginPath();
      ctx.ellipse(px, 33, 2.5, 3, 0, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  function ghostPup(ctx, x, y, scale) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.lineWidth = 3;
    ctx.fillStyle = '#d6a267';
    ctx.strokeStyle = '#aa784b';
    ctx.beginPath();
    ctx.moveTo(141, 160);
    ctx.bezierCurveTo(173, 184, 191, 157, 178, 144);
    ctx.bezierCurveTo(173, 141, 166, 147, 174, 153);
    ctx.bezierCurveTo(178, 162, 161, 167, 149, 153);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    [70, 109].forEach(px => {
      roundRect(ctx, px, 180, 28, 40, 10);
      ctx.fill();
      ctx.stroke();
    });
    ctx.fillStyle = '#fff4df';
    ctx.strokeStyle = '#c9ac83';
    ctx.beginPath();
    ctx.moveTo(54, 78);
    ctx.bezierCurveTo(54, 10, 148, 10, 148, 78);
    ctx.lineTo(157, 189);
    ctx.quadraticCurveTo(147, 202, 133, 194);
    ctx.quadraticCurveTo(118, 183, 106, 195);
    ctx.quadraticCurveTo(91, 183, 78, 194);
    ctx.quadraticCurveTo(60, 187, 44, 195);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#d6a267';
    ctx.strokeStyle = '#aa784b';
    [[39, 91, -0.16], [162, 91, 0.16]].forEach(([px, py, rotation]) => {
      ctx.beginPath();
      ctx.ellipse(px, py, 16, 43, rotation, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    });
    ctx.fillStyle = '#302134';
    [82, 119].forEach(px => {
      ctx.beginPath();
      ctx.ellipse(px, 79, 6, 8, 0, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.fillStyle = '#ead1ab';
    ctx.beginPath();
    ctx.ellipse(100, 109, 27, 20, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#302134';
    ctx.beginPath();
    ctx.moveTo(89, 97);
    ctx.lineTo(111, 97);
    ctx.lineTo(100, 109);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#302134';
    ctx.beginPath();
    ctx.moveTo(100, 108);
    ctx.lineTo(100, 115);
    ctx.quadraticCurveTo(91, 125, 84, 117);
    ctx.moveTo(100, 115);
    ctx.quadraticCurveTo(109, 125, 116, 117);
    ctx.stroke();
    ctx.restore();
  }

  function fitText(ctx, text, initialSize, minSize, width, family) {
    let size = initialSize;
    ctx.font = `500 ${size}px ${family}`;
    while (size > minSize && ctx.measureText(text).width > width) {
      size -= 1;
      ctx.font = `500 ${size}px ${family}`;
    }
  }

  function lantern(ctx, x, y) {
    ctx.save();
    const glow = ctx.createRadialGradient(x, y + 60, 5, x, y + 60, 92);
    glow.addColorStop(0, '#ffdc9266');
    glow.addColorStop(1, '#ffdc9200');
    ctx.fillStyle = glow;
    ctx.fillRect(x - 92, y - 32, 184, 184);
    ctx.lineWidth = 5;
    ctx.strokeStyle = '#3d261e';
    ctx.beginPath();
    ctx.moveTo(x, y - 30);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.fillStyle = '#efbd72';
    roundRect(ctx, x - 22, y + 22, 44, 78, 7);
    ctx.fill();
    ctx.fillStyle = '#fff2bd';
    ctx.beginPath();
    ctx.ellipse(x, y + 65, 8, 22, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#3d261e';
    ctx.strokeRect(x - 22, y + 22, 44, 78);
    ctx.beginPath();
    ctx.moveTo(x - 31, y + 22);
    ctx.lineTo(x + 31, y + 22);
    ctx.moveTo(x - 31, y + 100);
    ctx.lineTo(x + 31, y + 100);
    ctx.moveTo(x, y + 22);
    ctx.lineTo(x, y + 100);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 28, y + 22);
    ctx.lineTo(x - 14, y + 5);
    ctx.lineTo(x + 14, y + 5);
    ctx.lineTo(x + 28, y + 22);
    ctx.closePath();
    ctx.fillStyle = '#3d261e';
    ctx.fill();
    ctx.restore();
  }

  function cornerWeb(ctx, mirrored = false) {
    ctx.save();
    if (mirrored) {
      ctx.translate(SIZE, 0);
      ctx.scale(-1, 1);
    }
    ctx.strokeStyle = '#f3dfb17d';
    ctx.lineWidth = 1.6;
    [0, Math.PI / 8, Math.PI / 4, Math.PI * 3 / 8, Math.PI / 2].forEach(angle => {
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(angle) * 190, Math.sin(angle) * 190);
      ctx.stroke();
    });
    [45, 90, 135, 180].forEach(radius => {
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      for (let part = 0; part < 4; part += 1) {
        const start = part * Math.PI / 8;
        const end = (part + 1) * Math.PI / 8;
        const middle = (start + end) / 2;
        ctx.quadraticCurveTo(Math.cos(middle) * radius * 0.87, Math.sin(middle) * radius * 0.87, Math.cos(end) * radius, Math.sin(end) * radius);
      }
      ctx.stroke();
    });
    ctx.restore();
  }

  function drawnPorch(ctx) {
    const wall = ctx.createLinearGradient(0, 0, SIZE, SIZE);
    wall.addColorStop(0, '#a46d42');
    wall.addColorStop(0.5, '#755034');
    wall.addColorStop(1, '#422d27');
    ctx.fillStyle = wall;
    ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.strokeStyle = '#30201b40';
    ctx.lineWidth = 3;
    for (let x = 0; x <= SIZE; x += 54) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, 910);
      ctx.stroke();
      ctx.strokeStyle = '#e1aa6230';
      ctx.beginPath();
      ctx.moveTo(x + 9, 0);
      ctx.bezierCurveTo(x + 3, 240, x + 16, 620, x + 7, 910);
      ctx.stroke();
      ctx.strokeStyle = '#30201b40';
    }
    const floor = ctx.createLinearGradient(0, 850, 0, SIZE);
    floor.addColorStop(0, '#95613b');
    floor.addColorStop(1, '#553927');
    ctx.fillStyle = floor;
    ctx.fillRect(0, 850, SIZE, SIZE - 850);
    ctx.strokeStyle = '#33221b70';
    for (let y = 872; y < SIZE; y += 39) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(SIZE, y);
      ctx.stroke();
    }
    for (let x = -300; x < SIZE + 350; x += 150) {
      ctx.beginPath();
      ctx.moveTo(SIZE / 2 + (x - SIZE / 2) * 0.65, 850);
      ctx.lineTo(x, SIZE);
      ctx.stroke();
    }
    [109, 925].forEach(x => {
      const post = ctx.createLinearGradient(x, 0, x + 46, 0);
      post.addColorStop(0, '#4d3325');
      post.addColorStop(0.4, '#bc8851');
      post.addColorStop(1, '#593c2a');
      ctx.fillStyle = post;
      ctx.fillRect(x, 0, 46, 916);
      ctx.fillStyle = '#c39158';
      ctx.fillRect(x - 8, 889, 62, 30);
    });
    ctx.fillStyle = '#3b2924';
    ctx.beginPath();
    ctx.moveTo(164, 875);
    ctx.lineTo(164, 385);
    ctx.bezierCurveTo(164, -30, 916, -30, 916, 385);
    ctx.lineTo(916, 875);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#be8b50';
    ctx.lineWidth = 14;
    ctx.stroke();
    lantern(ctx, 135, 255);
    lantern(ctx, 946, 255);
    pumpkin(ctx, 77, 846, 1.6);
    pumpkin(ctx, 156, 898, 1);
    pumpkin(ctx, 1002, 851, 1.55);
    pumpkin(ctx, 927, 899, 1);
    cornerWeb(ctx);
    cornerWeb(ctx, true);
  }

  function porchScene(state, frame) {
    const ctx = state.ctx;
    if (state.backgroundImage) {
      const image = state.backgroundImage;
      const imageWidth = image.naturalWidth || image.width;
      const imageHeight = image.naturalHeight || image.height;
      const scale = Math.max(SIZE / imageWidth, SIZE / imageHeight);
      const width = imageWidth * scale;
      const height = imageHeight * scale;
      ctx.drawImage(image, (SIZE - width) / 2, (SIZE - height) / 2, width, height);
    } else drawnPorch(ctx);
    if (!state.backgroundImage && frame !== 'pumpkin') {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, SIZE, SIZE);
      ctx.rect(PLATE.x, PLATE.y, PLATE.width, PLATE.height);
      ctx.clip('evenodd');
      ctx.fillStyle = frame === 'ghost' ? '#39213e5c' : '#10142b87';
      ctx.fillRect(0, 0, SIZE, SIZE);
      ctx.restore();
    }
  }

  function loadPorchBackground(entry) {
    return new Promise((resolve, reject) => {
      let url;
      try {
        url = new URL(entry.url, window.location.href);
        if (!['https:', 'http:'].includes(url.protocol)) throw new Error('PORCH_URL');
      } catch (error) {
        reject(error);
        return;
      }
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      let finished = false;
      const finish = (error) => {
        if (finished) return;
        finished = true;
        window.clearTimeout(timeout);
        image.onload = null;
        image.onerror = null;
        entry.cancel = null;
        if (error) {
          image.src = '';
          reject(error);
        } else resolve(image);
      };
      const timeout = window.setTimeout(() => finish(new Error('PORCH_TIMEOUT')), 12000);
      entry.cancel = () => finish(new Error('PORCH_CANCELLED'));
      image.onload = () => {
        try {
          if (!image.naturalWidth || !image.naturalHeight) throw new Error('PORCH_DECODE');
          const probe = document.createElement('canvas');
          probe.width = 1;
          probe.height = 1;
          const ctx = probe.getContext('2d');
          if (!ctx) throw new Error('PORCH_CANVAS');
          ctx.drawImage(image, 0, 0, 1, 1);
          ctx.getImageData(0, 0, 1, 1);
          finish();
        } catch (error) { finish(error); }
      };
      image.onerror = () => finish(new Error('PORCH_LOAD'));
      image.src = url.href;
    });
  }

  function ensurePorchBackground(state) {
    if (state.disposed) return Promise.resolve(null);
    state.backgroundStarted = true;
    if (state.backgroundObserver) state.backgroundObserver.disconnect();
    const frame = selectedFrame(state);
    const source = message(state, backgroundKeys[frame]);
    let entry = state.backgroundCache.get(frame);
    if (!entry || entry.url !== source) {
      if (entry?.cancel) entry.cancel();
      if (entry?.image) entry.image.src = '';
      entry = { frame, url: source, key: `${frame}:${source}`, status: source ? 'idle' : 'missing', image: null, promise: null, cancel: null };
      state.backgroundCache.set(frame, entry);
    }
    if (state.activeBackgroundKey !== entry.key) {
      state.activeBackgroundKey = entry.key;
      state.backgroundWaiters.forEach(wake => wake());
    }
    if (entry.status === 'idle') {
      entry.status = 'loading';
      entry.promise = loadPorchBackground(entry).then(image => {
        entry.image = image;
        entry.status = 'ready';
        return image;
      }).catch(() => {
        entry.image = null;
        entry.status = 'error';
        return null;
      });
    }
    const updateActive = () => {
      if (state.disposed || state.activeBackgroundKey !== entry.key || selectedFrame(state) !== frame || state.backgroundCache.get(frame) !== entry) return;
      state.backgroundImage = entry.image;
      state.backgroundLoading = entry.status === 'loading';
      state.backgroundFailed = entry.status === 'error';
      if (!state.loading && ['', 'backgroundBusy', 'backgroundError', 'ready', 'downloadReady', 'cleared'].includes(state.statusKey)) {
        if (state.backgroundLoading) setStatus(state, 'backgroundBusy');
        else if (state.backgroundFailed) setStatus(state, 'backgroundError');
        else if (['backgroundBusy', 'backgroundError'].includes(state.statusKey)) setStatus(state, state.image ? 'ready' : '');
      }
      render(state);
    };
    updateActive();
    state.backgroundPromise = entry.promise || Promise.resolve(entry.image);
    if (entry.status === 'loading') state.backgroundPromise = state.backgroundPromise.then(image => {
      updateActive();
      return image;
    });
    return state.backgroundPromise;
  }

  async function waitForPorchBackground(state) {
    const pending = ensurePorchBackground(state);
    let wake;
    const changed = new Promise(resolve => { wake = resolve; });
    state.backgroundWaiters.add(wake);
    try { await Promise.race([pending, changed]); }
    finally { state.backgroundWaiters.delete(wake); }
  }

  function render(state) {
    const ctx = state.ctx;
    const frame = selectedFrame(state);
    const palette = palettes[frame];
    ctx.save();
    ctx.scale(OUTPUT_SIZE / SIZE, OUTPUT_SIZE / SIZE);
    ctx.clearRect(0, 0, SIZE, SIZE);
    porchScene(state, frame);
    ctx.strokeStyle = palette.line;
    ctx.lineWidth = 5;

    ctx.save();
    ctx.beginPath();
    ctx.rect(PHOTO.x, PHOTO.y, PHOTO.width, PHOTO.height);
    ctx.clip();
    ctx.fillStyle = palette.photo;
    ctx.fillRect(PHOTO.x, PHOTO.y, PHOTO.width, PHOTO.height);
    if (state.image) {
      const fill = state.fit?.value === 'cover';
      const scale = (fill ? Math.max : Math.min)(PHOTO.width / state.image.width, PHOTO.height / state.image.height);
      const width = state.image.width * scale;
      const height = state.image.height * scale;
      const x = PHOTO.x + (PHOTO.width - width) * (fill ? number(state.x?.value) / 100 : 0.5);
      const y = PHOTO.y + (PHOTO.height - height) * (fill ? number(state.y?.value) / 100 : 0.5);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(state.image, x, y, width, height);
    } else {
      paw(ctx, SIZE / 2, 410, 1.8, palette.line);
      ctx.fillStyle = palette.ink;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const placeholder = message(state, 'previewPlaceholder');
      fitText(ctx, placeholder, 28, 16, PHOTO.width - 80, 'Arial, Helvetica, sans-serif');
      ctx.fillText(placeholder, SIZE / 2, 515, PHOTO.width - 80);
    }
    ctx.restore();
    ctx.strokeStyle = palette.line;
    ctx.lineWidth = 4;
    ctx.strokeRect(PHOTO.x - 4, PHOTO.y - 4, PHOTO.width + 8, PHOTO.height + 8);

    if (!state.backgroundImage && frame === 'pumpkin') {
      paw(ctx, 66, 76, 0.65, '#f7dfb8');
    } else if (!state.backgroundImage && frame === 'ghost') {
      ghostPup(ctx, 24, 668, 0.7);
      ghostPup(ctx, 974, 748, 0.4);
    } else if (!state.backgroundImage) {
      bat(ctx, 24, 42, 1.1, palette.accent);
      bat(ctx, 951, 674, 0.8, palette.accent);
      ctx.save();
      ctx.fillStyle = palette.accent;
      ctx.beginPath();
      ctx.arc(998, 64, 43, 0, Math.PI * 2);
      ctx.fill();
      ctx.clip();
      ctx.fillStyle = '#221c32';
      ctx.beginPath();
      ctx.arc(1015, 49, 39, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    if (!state.backgroundImage) {
      ctx.fillStyle = '#f5e7cc';
      roundRect(ctx, PLATE.x, PLATE.y, PLATE.width, PLATE.height, 6);
      ctx.fill();
      ctx.strokeStyle = '#b79063';
      ctx.lineWidth = 3;
      ctx.stroke();
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#302435';
    const name = photoName(state);
    fitText(ctx, name, 49, 24, 536, "Georgia, 'Times New Roman', serif");
    ctx.fillText(name, SIZE / 2, 832, 536);
    fitText(ctx, message(state, 'occasion'), 23, 16, 536, "Georgia, 'Times New Roman', serif");
    ctx.fillText(message(state, 'occasion'), SIZE / 2, 875, 536);
    fitText(ctx, message(state, 'brand'), 14, 11, 536, 'Arial, Helvetica, sans-serif');
    ctx.fillText(message(state, 'brand'), SIZE / 2, 895, 536);
    ctx.restore();
    const label = message(state, 'previewLabel');
    if (label) state.canvas.setAttribute('aria-label', label.replaceAll('{name}', name));
    state.renderRevision += 1;
    syncControls(state);
  }

  async function sniff(file) {
    const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
    if ([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((byte, index) => bytes[index] === byte)) return 'png';
    if (String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP') return 'webp';
    return null;
  }

  async function decode(file) {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.decoding = 'async';
    try {
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error('PHOTO_DECODE'));
        image.src = url;
      });
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      if (!width || !height) throw new Error('PHOTO_DECODE');
      if (width > MAX_EDGE || height > MAX_EDGE || width * height > MAX_PIXELS) throw new Error('PHOTO_RESOLUTION');
      const scale = Math.min(1, WORK_EDGE / Math.max(width, height));
      const working = document.createElement('canvas');
      working.width = Math.max(1, Math.round(width * scale));
      working.height = Math.max(1, Math.round(height * scale));
      const ctx = working.getContext('2d');
      if (!ctx) throw new Error('PHOTO_DECODE');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, 0, 0, working.width, working.height);
      return working;
    } finally {
      image.onload = null;
      image.onerror = null;
      image.src = '';
      URL.revokeObjectURL(url);
    }
  }

  function releasePhoto(state) {
    if (state.image) {
      state.image.width = 0;
      state.image.height = 0;
    }
    state.image = null;
  }

  async function upload(state) {
    const sequence = ++state.sequence;
    const file = state.file.files?.[0];
    if (file) ensurePorchBackground(state);
    releasePhoto(state);
    state.loading = false;
    state.exporting = false;
    if (state.x) state.x.value = '50';
    if (state.y) state.y.value = '50';
    render(state);
    if (!file) {
      setStatus(state, 'cleared');
      return;
    }
    if (file.size > MAX_BYTES) {
      setStatus(state, 'tooLarge', true);
      state.file.value = '';
      return;
    }
    state.loading = true;
    setStatus(state, 'busy');
    syncControls(state);
    try {
      if (!await sniff(file)) throw new Error('PHOTO_FORMAT');
      if (sequence !== state.sequence) return;
      const image = await decode(file);
      if (sequence !== state.sequence) {
        image.width = 0;
        image.height = 0;
        return;
      }
      state.image = image;
      setStatus(state, state.backgroundLoading ? 'backgroundBusy' : state.backgroundFailed ? 'backgroundError' : 'ready');
    } catch (error) {
      if (sequence !== state.sequence) return;
      const key = error.message === 'PHOTO_FORMAT' ? 'formatError' : error.message === 'PHOTO_RESOLUTION' ? 'resolutionError' : 'uploadError';
      setStatus(state, key, true);
      state.file.value = '';
    } finally {
      if (sequence === state.sequence) {
        state.loading = false;
        render(state);
      }
    }
  }

  const filenamePart = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 70);

  async function download(state) {
    if (!state.image || state.loading || state.exporting || state.disposed) return;
    const sequence = state.sequence;
    const png = state.format?.value === 'png';
    state.exporting = true;
    syncControls(state);
    try {
      const encode = () => new Promise((resolve, reject) => {
        state.canvas.toBlob(result => result ? resolve(result) : reject(new Error('PHOTO_EXPORT')), png ? 'image/png' : 'image/jpeg', 0.94);
      });
      let blob;
      let name;
      while (!state.disposed && sequence === state.sequence && state.image && !state.loading) {
        const frame = selectedFrame(state);
        await waitForPorchBackground(state);
        if (state.disposed || sequence !== state.sequence || !state.image || state.loading) return;
        if (frame !== selectedFrame(state) || state.backgroundLoading) continue;
        render(state);
        const revision = state.renderRevision;
        name = photoName(state);
        try { blob = await encode(); }
        catch (error) {
          if (error.name !== 'SecurityError' || !state.backgroundImage) throw error;
          const entry = state.backgroundCache.get(frame);
          if (entry?.image) entry.image.src = '';
          if (entry) {
            entry.image = null;
            entry.status = 'error';
            entry.promise = Promise.resolve(null);
          }
          state.backgroundImage = null;
          state.backgroundFailed = true;
          state.canvas.width = OUTPUT_SIZE;
          state.canvas.height = OUTPUT_SIZE;
          render(state);
          const fallbackRevision = state.renderRevision;
          blob = await encode();
          if (frame !== selectedFrame(state) || fallbackRevision !== state.renderRevision) continue;
          break;
        }
        if (frame === selectedFrame(state) && revision === state.renderRevision) break;
      }
      if (!blob || state.disposed || sequence !== state.sequence) return;
      const filename = [filenamePart(message(state, 'filePrefix')), filenamePart(name)].filter(Boolean).join('-') || String(Date.now());
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.${png ? 'png' : 'jpg'}`;
      link.hidden = true;
      document.body.append(link);
      try {
        link.click();
      } finally {
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      }
      setStatus(state, 'downloadReady');
    } catch (_error) {
      if (sequence === state.sequence) setStatus(state, 'downloadError', true);
    } finally {
      if (sequence === state.sequence) {
        state.exporting = false;
        syncControls(state);
      }
    }
  }

  function initialize(root) {
    if (instances.has(root)) return;
    const canvas = root.querySelector('[data-howl-photo-canvas]');
    const file = root.querySelector('[data-howl-photo-file]');
    const downloadButton = root.querySelector('[data-howl-photo-download]');
    if (!canvas || !file || !downloadButton) return;
    const ctx = canvas.getContext('2d');
    if (!ctx || !URL.createObjectURL || !canvas.toBlob) {
      downloadButton.disabled = true;
      file.disabled = true;
      const fallback = root.querySelector('[data-howl-photo-fallback]');
      if (fallback) fallback.hidden = false;
      return;
    }
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const state = {
      root, canvas, ctx, file, download: downloadButton,
      name: root.querySelector('[data-howl-photo-name]'),
      frames: Array.from(root.querySelectorAll('[data-howl-photo-frame]')),
      fit: root.querySelector('[data-howl-photo-fit]'),
      x: root.querySelector('[data-howl-photo-x]'),
      y: root.querySelector('[data-howl-photo-y]'),
      position: root.querySelector('[data-howl-photo-position]'),
      format: root.querySelector('[data-howl-photo-format]'),
      clear: root.querySelector('[data-howl-photo-clear]'),
      status: root.querySelector('[data-howl-photo-status]'),
      image: null, loading: false, exporting: false, sequence: 0,
      backgroundImage: null, backgroundLoading: false, backgroundStarted: false,
      backgroundPromise: null, backgroundObserver: null, backgroundFailed: false,
      backgroundCache: new Map(), activeBackgroundKey: '', renderRevision: 0,
      backgroundWaiters: new Set(),
      disposed: false, statusKey: ''
    };
    instances.set(root, state);
    const controls = root.querySelector('[data-howl-photo-controls]');
    const preview = root.querySelector('[data-howl-photo-preview]');
    if (controls) controls.hidden = false;
    const fallback = root.querySelector('[data-howl-photo-fallback]');
    if (fallback) fallback.hidden = true;
    file.addEventListener('change', () => upload(state));
    [state.name, state.fit, state.x, state.y].filter(Boolean).forEach(control => {
      control.addEventListener('input', () => render(state));
      control.addEventListener('change', () => render(state));
    });
    state.frames.forEach(control => {
      control.addEventListener('input', () => ensurePorchBackground(state));
      control.addEventListener('change', () => ensurePorchBackground(state));
    });
    downloadButton.addEventListener('click', () => download(state));
    if (state.clear) state.clear.addEventListener('click', () => {
      state.sequence += 1;
      state.loading = false;
      state.exporting = false;
      state.backgroundWaiters.forEach(wake => wake());
      releasePhoto(state);
      file.value = '';
      if (state.x) state.x.value = '50';
      if (state.y) state.y.value = '50';
      render(state);
      setStatus(state, 'cleared');
    });
    render(state);
    if (preview) preview.hidden = false;
    if (Object.values(backgroundKeys).some(key => message(state, key)) && typeof IntersectionObserver === 'function') {
      state.backgroundObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) ensurePorchBackground(state);
      }, { rootMargin: '500px 0px' });
      state.backgroundObserver.observe(root);
    }
  }

  function initializeWithin(container) {
    if (container.matches?.('[data-howl-photo-booth]')) initialize(container);
    container.querySelectorAll('[data-howl-photo-booth]').forEach(initialize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  else initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const roots = event.target.matches?.('[data-howl-photo-booth]') ? [event.target] : Array.from(event.target.querySelectorAll('[data-howl-photo-booth]'));
    roots.forEach(root => {
      const state = instances.get(root);
      if (!state) return;
      state.disposed = true;
      state.sequence += 1;
      state.loading = false;
      state.backgroundWaiters.forEach(wake => wake());
      state.backgroundCache.forEach(entry => {
        if (entry.cancel) entry.cancel();
        if (entry.image) entry.image.src = '';
      });
      state.backgroundCache.clear();
      if (state.backgroundObserver) state.backgroundObserver.disconnect();
      state.backgroundImage = null;
      releasePhoto(state);
      instances.delete(root);
    });
  });
})();
